import { addMinutes } from 'date-fns';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import type { DatePickerDateType, SelectProperties } from 'ui';
import { DurationOptionLabel } from '../duration-option-label';
import { useGetStartDateTime } from './useGetStartDateTime';

const ADMIN_DURATION_LIMIT = 12 * 60;
const USER_DURATION_LIMIT = 60;

const formatDurationLabel = (duration: number, t: TFunction) => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  if (!hours) {
    return `${minutes} ${t('min')}`;
  }

  return t('hr', { count: duration / 60 });
};

const formatEndTimeLabel = ({ duration, start, t }: { duration: number; start?: Date; t: TFunction }) => {
  if (!start) {
    return undefined;
  }

  const end = addMinutes(start, duration);
  return t('dateFormats.time', { date: end });
};

export const useCreateDurationOptions = ({
  startDate,
  startTime,
}: {
  startDate?: DatePickerDateType;
  startTime?: string;
}): SelectProperties['options'] => {
  const { t } = useTranslation();
  const { isEventsAdmin } = usePermissions();
  const maximumDuration = isEventsAdmin({}) ? ADMIN_DURATION_LIMIT : USER_DURATION_LIMIT;
  const getStartDateTime = useGetStartDateTime();

  return useMemo<SelectProperties['options']>(
    () =>
      [15, 30, 45, ...Array.from({ length: 23 }, (_, index) => 60 + index * 30)]
        .filter((duration) => duration <= maximumDuration)
        .map((duration) => {
          const start = startDate && startTime ? getStartDateTime({ startDate, startTime }) : undefined;
          const end = start ? addMinutes(start, duration) : undefined;
          const durationLabel = formatDurationLabel(duration, t);
          const endTimeLabel = start ? formatEndTimeLabel({ duration, start, t }) : undefined;
          const optionLabel = endTimeLabel ? `${durationLabel} (${endTimeLabel})` : durationLabel;

          return {
            label: <DurationOptionLabel end={end} mainLabel={optionLabel} start={start} />,
            optionLabel,
            value: duration,
          };
        }),
    [maximumDuration, t, getStartDateTime, startDate, startTime],
  );
};
